import { operationKey, uploadOperationKey } from './api.js'

export async function priorityPreviewRequest(file, context = null) {
  const form = new globalThis.FormData()
  form.append('file', file)
  let suffix = ''
  if (context) {
    const workspace = Number(context.workspace_version)
    const snapshot = Number(context.active_snapshot_version)
    const profile = String(context.parser_profile || '')
    if (!Number.isInteger(workspace) || workspace < 1 || !Number.isInteger(snapshot) || snapshot < 0 || !profile) {
      throw new Error('Refresh Priority status before uploading')
    }
    form.append('expected_workspace_version', String(workspace))
    form.append('expected_snapshot_version', String(snapshot))
    form.append('expected_parser_profile', profile)
    suffix = `:workspace-${workspace}:snapshot-${snapshot}:profile-${profile}`
  }
  return {
    body: form,
    idempotencyKey: `priority:${await uploadOperationKey(file)}${suffix}`,
  }
}

export function priorityApplyRequest(resource) {
  if (!resource?.import_id) throw new Error('A staged Priority import is required')
  const idempotencyKey = operationKey(
    'priority-apply',
    resource.import_id,
    resource.version,
  )
  return {
    idempotencyKey,
    body: {
      import_version: Number(resource.version),
      expected_workspace_version: Number(resource.expected_workspace_version),
      expected_snapshot_version: Number(resource.expected_snapshot_version),
      confirmed: true,
      idempotency_key: idempotencyKey,
    },
  }
}

export function emptyResetRequest(workspace) {
  const version = Number(workspace?.workspace_version)
  if (!Number.isInteger(version) || version < 1) {
    throw new Error('The current workspace version is unavailable')
  }
  const idempotencyKey = operationKey('reset-empty', 'workspace', version)
  return {
    idempotencyKey,
    body: {
      mode: 'empty',
      expected_workspace_version: version,
      idempotency_key: idempotencyKey,
    },
  }
}

export function previewCounts(resource) {
  const preview = resource?.preview || {}
  const importable = Number(preview.invoice_count || 0)
  return {
    sourceRows: Number(preview.source_row_count || 0),
    candidates: Number(preview.candidate_row_count || 0),
    blocked: Number(preview.blocked_candidate_row_count || 0),
    importable,
    invoices: importable,
    customers: Number(preview.customer_count || 0),
    companies: Number(preview.company_count || 0),
    exclusions: Number(preview.exclusion_count || 0),
    errors: Number(preview.error_count || 0),
  }
}

function issueRowNumbers(issue = {}) {
  const values = [issue.row_number, ...(Array.isArray(issue.related_row_numbers) ? issue.related_row_numbers : [])]
  return [...new Set(values.map(Number).filter((value) => Number.isInteger(value) && value > 0))]
    .sort((left, right) => left - right)
}

export function priorityIssueView(issue = {}) {
  const rowNumbers = issueRowNumbers(issue)
  const field = String(issue.field || '').trim()
  const entityReference = String(issue.entity_reference || '').trim()
  const location = rowNumbers.length
    ? `${rowNumbers.length === 1 ? 'row' : 'rows'} ${rowNumbers.join(', ')}`
    : 'the workbook'
  const fieldContext = field ? ` in ${field}` : ''
  return {
    code: String(issue.code || 'workbook_validation_issue'),
    rowNumbers,
    field,
    entityReference,
    message: String(issue.message || '').trim()
      || `Review the validation issue at ${location}${fieldContext}.`,
    resolution: String(issue.resolution || '').trim()
      || 'Correct the source value and upload the workbook again.',
  }
}

export function priorityErrorsPath(resource) {
  const serverPath = String(resource?.errors_path || '').trim().replace(/^\/+/, '')
  if (serverPath) return serverPath
  const importId = String(resource?.import_id || '').trim()
  return importId
    ? `integrations/priority/imports/${encodeURIComponent(importId)}/errors`
    : null
}

function priorityIssueKey(issue) {
  const view = priorityIssueView(issue)
  return JSON.stringify([
    view.code,
    view.rowNumbers,
    view.field,
    view.entityReference,
  ])
}

export function mergePriorityIssues(current = [], incoming = []) {
  const merged = []
  const seen = new Set()
  for (const issue of [...current, ...incoming]) {
    const key = priorityIssueKey(issue)
    if (seen.has(key)) continue
    seen.add(key)
    merged.push(issue)
  }
  return merged
}


export function priorityOperationStorageKey(workspaceId, userId) {
  if (!workspaceId || !userId) return null
  return `collections:priority-upload:${encodeURIComponent(workspaceId)}:${encodeURIComponent(userId)}`
}

export function readPriorityOperation(storage, key) {
  if (!key) return null
  try {
    const value = JSON.parse(storage?.getItem(key) || 'null')
    return value?.schema_version === 1 && typeof value.preview_key === 'string' ? value : null
  } catch { return null }
}

export function writePriorityOperation(storage, key, operation) {
  if (!key) return
  try {
    if (operation) storage?.setItem(key, JSON.stringify(operation))
    else storage?.removeItem(key)
  } catch { /* Private-mode storage may be unavailable; the server remains durable. */ }
}

export function priorityReconciliationFrom(error) {
  // The API puts the human-readable detail beside top-level recovery fields;
  // older adapters may instead nest the structured error. Ignore text values.
  return [error?.data, error?.data?.detail, error?.data?.error]
    .find((value) => value && typeof value === 'object' && value.reconciliation_id) || null
}

export function priorityUploadContext(status) {
  return { ...status?.workspace, parser_profile: status?.parser_profile || status?.snapshot?.parser_profile }
}

async function finishPriorityRecord(record, operation, options) {
  const { requester, persist = () => {}, onRecord = () => {}, onPhase = () => {} } = options
  operation = { ...operation, import_id: record.import_id }
  persist(operation); onRecord(record)
  if (record.status === 'invalid') {
    persist(null)
    return { state: 'invalid', record }
  }
  if (record.status === 'applied') {
    const status = await requester('integrations/priority/status')
    const activeId = status?.workspace?.active_import_id || status?.snapshot?.import_id
    persist(null)
    return { state: activeId === record.import_id ? 'applied' : 'historical', record }
  }
  if (record.status === 'applying') return { state: 'pending', record, operation }
  if (record.status !== 'staged') throw new Error('The Priority import cannot be applied in its current state')
  const applyRequest = operation.apply_request || priorityApplyRequest(record)
  operation = { ...operation, apply_request: applyRequest }
  persist(operation); onPhase('apply')
  try {
    const applied = await requester(`integrations/priority/imports/${encodeURIComponent(record.import_id)}/apply`, {
      method: 'POST', headers: { 'Idempotency-Key': applyRequest.idempotencyKey }, body: applyRequest.body,
    })
    return await finishPriorityRecord(applied, operation, options)
  } catch (error) {
    const reconciliation = priorityReconciliationFrom(error)
    if (reconciliation) return { state: 'reconciliation', record, operation, reconciliation }
    throw error
  }
}

export async function resumePriorityUpload(operation, options) {
  if (!operation) return { state: 'idle' }
  const { requester, persist = () => {} } = options
  let record
  try {
    record = operation.import_id
      ? await requester(`integrations/priority/imports/${encodeURIComponent(operation.import_id)}`)
      : await requester('integrations/priority/imports/lookup', { params: { idempotency_key: operation.preview_key } })
  } catch (error) {
    if (error.status === 404) return { state: 'needs_file', operation }
    throw error
  }
  if (!record) return { state: 'needs_file', operation }
  if (record.status === 'staged') {
    const status = await requester('integrations/priority/status')
    if (Number(status?.workspace?.workspace_version) !== Number(record.expected_workspace_version)
      || Number(status?.workspace?.active_snapshot_version) !== Number(record.expected_snapshot_version)) {
      persist(null)
      return { state: 'stale', record }
    }
  }
  return finishPriorityRecord(record, operation, options)
}

export async function runPriorityUpload(file, status, options) {
  const { requester, persist = () => {}, onPhase = () => {} } = options
  const preview = await priorityPreviewRequest(file, priorityUploadContext(status))
  let operation = options.operation?.preview_key === preview.idempotencyKey ? options.operation : {
    schema_version: 1, preview_key: preview.idempotencyKey,
    filename: file.name || 'priority.xlsx', import_id: null, apply_request: null,
  }
  persist(operation); onPhase('preview')
  try {
    const record = await requester('integrations/priority/imports', {
      method: 'POST', headers: { 'Idempotency-Key': operation.preview_key }, body: preview.body,
    })
    return await finishPriorityRecord(record, operation, options)
  } catch (error) {
    // Only ambiguous transport/server failures need durable-state lookup.
    // Validation/version conflicts are never automatically retried with new fences.
    if (!error.status || error.status >= 500) {
      try {
        return await resumePriorityUpload({ ...operation, ...options.currentOperation?.() }, options)
      } catch { /* Preserve original error and saved operation for explicit retry. */ }
    } else if (error.status >= 400 && error.status < 500) {
      persist(null)
    }
    throw error
  }
}
